/**
 * Newsletter Subscription Service
 * 
 * Handles newsletter email subscriptions
 */

import { safeFrom, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notConfiguredError
} from '@/lib/service-errors';
import { emailService } from './emailService';

export interface NewsletterSubscription {
  id: number;
  email: string;
  name?: string;
  subscribed_at: string;
  unsubscribed_at?: string;
  is_active: boolean;
}

export interface SubscribeData {
  email: string;
  name?: string;
}

/**
 * Subscribe to newsletter
 */
export const subscribeToNewsletter = async (
  data: SubscribeData
): Promise<ServiceResponse<NewsletterSubscription>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    // Validate email
    if (!data.email || !data.email.trim()) {
      return errorResponse({
        code: 'VALIDATION_ERROR',
        message: 'Email address is required',
        recoverable: true
      });
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email.trim())) {
      return errorResponse({
        code: 'VALIDATION_ERROR',
        message: 'Invalid email address',
        recoverable: true
      });
    }

    const email = data.email.trim().toLowerCase();
    const name = data.name?.trim() || null;

    // Check if email already exists
    const { data: existing, error: checkError } = await safeFrom('newsletter_subscribers')
      .select('id, email, is_active')
      .eq('email', email)
      .maybeSingle();

    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
      throw checkError;
    }

    // If exists and active, return success
    if (existing && existing.is_active) {
      return successResponse(existing as NewsletterSubscription);
    }

    // If exists but inactive, reactivate
    if (existing && !existing.is_active) {
      const { data: updated, error: updateError } = await safeFrom('newsletter_subscribers')
        .update({
          is_active: true,
          name: name,
          unsubscribed_at: null,
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (updateError) {
        throw updateError;
      }

      return successResponse(updated as NewsletterSubscription);
    }

    // Create new subscription
    const { data: subscription, error: insertError } = await safeFrom('newsletter_subscribers')
      .insert([{
        email,
        name,
        is_active: true,
      }])
      .select()
      .single();

    if (insertError) {
      throw insertError;
    }

    const created = subscription as NewsletterSubscription;

    // Send welcome email (non-blocking)
    const orgName = import.meta.env.VITE_ORG_NAME || 'Our Organization';
    emailService.sendEmail({
      to: email,
      subject: `Welcome to ${orgName} Newsletter`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #333;">Thank you for subscribing!</h2>
          <p>Dear ${name || 'Subscriber'},</p>
          <p>Thank you for subscribing to our newsletter! You'll receive updates about our latest news, events, and programs.</p>
          <p>Best regards,<br>The ${orgName} Team</p>
        </div>
      `,
    }).catch(error => {
      console.error('[newsletterService] Failed to send welcome email:', error);
    });

    // Send notification to admin (non-blocking)
    const adminEmail = import.meta.env.VITE_ADMIN_EMAIL || 'admin@example.com';
    emailService.sendEmail({
      to: adminEmail,
      subject: `New Newsletter Subscription: ${email}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #333;">New Newsletter Subscription</h2>
          <p><strong>Email:</strong> ${email}</p>
          ${name ? `<p><strong>Name:</strong> ${name}</p>` : ''}
          <p><strong>Subscribed at:</strong> ${new Date().toLocaleString()}</p>
        </div>
      `,
    }).catch(error => {
      console.error('[newsletterService] Failed to send admin notification:', error);
    });

    return successResponse(created);
  } catch (error) {
    console.error('[newsletterService] Error subscribing to newsletter:', error);
    return errorResponse(handleServiceError(error, 'newsletterService.subscribeToNewsletter', { data }));
  }
};

/**
 * Unsubscribe from newsletter
 */
export const unsubscribeFromNewsletter = async (
  email: string
): Promise<ServiceResponse<void>> => {
  if (!isSupabaseAvailable()) {
    return errorResponse(notConfiguredError('Supabase'));
  }

  try {
    const { error } = await safeFrom('newsletter_subscribers')
      .update({
        is_active: false,
        unsubscribed_at: new Date().toISOString(),
      })
      .eq('email', email.toLowerCase().trim());

    if (error) {
      throw error;
    }

    return successResponse(undefined);
  } catch (error) {
    console.error('[newsletterService] Error unsubscribing from newsletter:', error);
    return errorResponse(handleServiceError(error, 'newsletterService.unsubscribeFromNewsletter', { email }));
  }
};

export const newsletterService = {
  subscribeToNewsletter,
  unsubscribeFromNewsletter,
};
